import { JobMode } from '../enums';
import { SecurityModuleName } from '../../security/security.module';
import { TenantPlan } from '../../security/tenant.plan';

/**
 * Job structure = the job's `mode`. One implementation shared by the web app and the API, so every
 * surface names, orders, entitles and resolves a new job's structure the same way (WBS-11720).
 */

/** How each structure is named to users. 'Advanced' stays the stored value; users see Multi-Stage. */
export const JOB_STRUCTURE_LABELS: Record<JobMode, string> = {
    Basic: 'Basic (single-stage)',
    Advanced: 'Multi-Stage',
    Project: 'Project'
};

/** Display order wherever structures are listed together. */
export const JOB_STRUCTURE_ORDER: readonly JobMode[] = ['Project', 'Advanced', 'Basic'];

/**
 * Structures the tenant has bought. Basic is always included; Multi-Stage and Project are separate
 * addons, so each is entitled on its own.
 */
export function getEntitledStructures(plan: Pick<TenantPlan, 'addons'> | null | undefined): JobMode[] {

    const entitled: JobMode[] = ['Basic'];

    if (plan?.addons?.[SecurityModuleName.MultiStageJobs]) entitled.push('Advanced');
    if (plan?.addons?.[SecurityModuleName.ProjectJobs]) entitled.push('Project');

    return entitled;
}

export type JobStructureSource = 'caller' | 'template' | 'jobType' | 'tenant';

export interface ResolvedJobStructure {
    mode: JobMode;
    source: JobStructureSource;
}

/**
 * A new job's structure: the caller's choice, else the template's, else the job type's default, else the
 * tenant's default. Entitlement is not applied here — the source is returned so the create validator can
 * refuse with a message naming whichever of the four supplied a structure the tenant has not bought.
 */
export function resolveJobStructure(inputs: {
    /** Structure named explicitly by whoever creates the job. */
    caller?: JobMode | null;
    /** The template's `mode`. */
    template?: JobMode | null;
    /** The job type's `defaultJobMode`. */
    jobType?: JobMode | null;
    /** The tenant's `operations.defaultJobStructure`, already read through its default. */
    tenant?: JobMode | null;
}): ResolvedJobStructure | null {

    // `!= null`: stored documents carry null as often as a missing key; both mean "not set".
    if (inputs.caller != null) return { mode: inputs.caller, source: 'caller' };
    if (inputs.template != null) return { mode: inputs.template, source: 'template' };
    if (inputs.jobType != null) return { mode: inputs.jobType, source: 'jobType' };
    if (inputs.tenant != null) return { mode: inputs.tenant, source: 'tenant' };

    return null;
}

/** The addon that sells each non-Basic structure. Basic is always entitled, so it has no entry. */
export const ADDON_FOR_STRUCTURE: Partial<Record<JobMode, SecurityModuleName>> = {
    Advanced: SecurityModuleName.MultiStageJobs,
    Project: SecurityModuleName.ProjectJobs,
};

/**
 * Why a picker entry is greyed: null when the structure is entitled, else the hover reason naming the addon
 * (WBS-11730). Courtesy only — the server keeps refusing the create.
 */
export function structureEntitlementIssue(mode: JobMode | null | undefined, entitled: readonly JobMode[]): string | null {

    if (mode == null || entitled.includes(mode)) return null;

    const addon = ADDON_FOR_STRUCTURE[mode];

    return addon ? `Needs the ${addon} addon` : `Unknown job structure '${mode}'`;
}

/** The tenant default structure's warning, shared by both General Settings pages: null when the plan still covers it. */
export function defaultStructureIssue(mode: JobMode | null | undefined, entitled: readonly JobMode[]): string | null {

    const issue = structureEntitlementIssue(mode, entitled);

    return issue ? `Default job structure '${JOB_STRUCTURE_LABELS[mode] ?? mode}': ${issue}.` : null;
}

/**
 * How a stored job reads on every surface (T7, WBS-11720): no backfill was run, so a missing, null or unknown
 * `mode` (production holds 'ADVANCED' and 'Standard') reads as Multi-Stage, and a Basic job that somehow grew a
 * second stage reads as Multi-Stage too. The stored value is never rewritten; the transition ladder still keys on it.
 */
export function effectiveMode(job: { mode?: string | null; workOrders?: readonly unknown[] | null; stageCount?: number | null } | null | undefined): JobMode {

    const stored = job?.mode;

    if (stored === 'Project') return 'Project';
    // Shapes that carry no stage array (stage search context) pass the count instead.
    if (stored === 'Basic') return (job?.stageCount ?? job?.workOrders?.length ?? 0) > 1 ? 'Advanced' : 'Basic';

    return 'Advanced';
}
